import {
  Prisma,
  prisma,
  ChatParticipantType,
  UserRoleAssignmentEntityType,
} from "client";

function asIdList(id: string | string[]): string[] {
  return Array.isArray(id) ? id.filter(Boolean) : id ? [id] : [];
}

/** Organizers assigned to orgs on this event. Indexed: Organization.eventIds, AssignmentModel [entityId, entityType]. */
export async function organizerIdsForEvent(eventId: string): Promise<string[]> {
  const orgs = await prisma.organization.findMany({
    where: { eventIds: { has: eventId } },
    select: { id: true },
  });
  const orgIds = orgs.map((org) => org.id).filter(Boolean);
  if (!orgIds.length) return [];
  const assigned = await prisma.assignmentModel.findMany({
    where: {
      entityType: UserRoleAssignmentEntityType.ORGANIZATION,
      entityId: { in: orgIds },
    },
    select: { userId: true },
  });
  return [...new Set(assigned.map((row) => row.userId))];
}

/** Collapse threads that are the same peer with my user-id vs company-id.
 *  Do not map peer USERs onto companies — that merges distinct team-member DMs. */
function dedupeChatsByLogicalPeer<
  T extends {
    id: string;
    participants: { participantId: string; type: ChatParticipantType }[];
    messages?: { createdAt: Date }[];
    createdAt?: Date;
  },
>(chats: T[], myIds: string[], preferredId?: string): T[] {
  if (chats.length <= 1) return chats;
  const mySet = new Set(myIds);
  const preferred =
    preferredId && mySet.has(preferredId) ? preferredId : myIds[0];

  const peerKey = (chat: T) =>
    chat.participants
      .filter((p) => !mySet.has(p.participantId))
      .map((p) => p.participantId)
      .sort()
      .join("|") ||
    chat.participants
      .map((p) => p.participantId)
      .sort()
      .join("|");

  const byPeer = new Map<string, T>();
  for (const chat of chats) {
    const key = peerKey(chat);
    const existing = byPeer.get(key);
    if (!existing) {
      byPeer.set(key, chat);
      continue;
    }
    const existingPreferred = existing.participants.some(
      (p) => p.participantId === preferred
    );
    const chatPreferred = chat.participants.some(
      (p) => p.participantId === preferred
    );
    if (chatPreferred && !existingPreferred) {
      byPeer.set(key, chat);
      continue;
    }
    if (existingPreferred && !chatPreferred) continue;
    const existingTs =
      existing.messages?.[0]?.createdAt ?? existing.createdAt ?? new Date(0);
    const chatTs = chat.messages?.[0]?.createdAt ?? chat.createdAt ?? new Date(0);
    if (chatTs > existingTs) byPeer.set(key, chat);
  }
  return [...byPeer.values()];
}

export const createChat = async ({
  name,
  eventId,
  participantIds,
}: {
  name: string;
  eventId: string;
  participantIds: {
    id: string;
    type: ChatParticipantType;
  }[];
}) => {
  try {
    const now = new Date();
    const chat = await prisma.chat.create({
      data: {
        name,
        lastMessageAt: now,
        participants: {
          create: participantIds.map((participant) => ({
            participantId: participant.id,
            type: participant.type,
          })),
        },
        event: {
          connect: {
            id: eventId,
          },
        },
      },
    });
    return chat;
  } catch (error) {
    console.error("Error creating chat:", error);
    throw new Error("Failed to create chat");
  }
};
export const getChatBySenderAndReceiverIds = async (
  senderId: string | string[],
  eventId: string,
  receiverId: string | string[]
) => {
  try {
    const senderIds = asIdList(senderId);
    const receiverIds = asIdList(receiverId);
    if (!senderIds.length || !receiverIds.length) return null;

    return await prisma.chat.findFirst({
      where: {
        AND: [
          {
            participants: {
              some: {
                participantId: { in: senderIds },
              },
            },
          },
          {
            participants: {
              some: {
                participantId: { in: receiverIds },
              },
            },
          },
          {
            eventId,
          },
        ],
      },
      include: {
        participants: {
          include: {
            exhibitor: {
              include: { translations: true },
            },
            sponsor: {
              include: { translations: true },
            },
            user: {
              include: {
                profiles: {
                  where: {
                    eventId,
                  },
                  orderBy: {
                    createdAt: "desc",
                  },
                  take: 1,
                },
              },
            },
          },
        },
        messages: {
          include: {
            exhibitor: {
              include: { translations: true },
            },
            sponsor: {
              include: { translations: true },
            },
            user: {
              include: {
                profiles: {
                  where: {
                    eventId,
                  },
                  orderBy: {
                    createdAt: "desc",
                  },
                  take: 1,
                },
              },
            },
          },
          orderBy: {
            createdAt: "asc",
          },
        },
      },
    });
  } catch (error) {
    console.error("Error fetching chat:", error);
    throw new Error("Failed to fetch chat");
  }
};

export const getChatsPaginated = async (
  userId: string | string[],
  eventId: string,
  page: number = 1,
  limit: number = 10,
  isAdminChats: boolean = false,
  /** Org/admin inboxes: only threads with a non-USER peer (exhibitor/sponsor/org). */
  companyFacingOnly: boolean = isAdminChats,
  /** Create missing USER↔ORGANIZATION threads for this person (admin booth inbox). */
  provisionOrganizerUserId?: string
) => {
  try {
    const entityIds = asIdList(userId);
    if (!entityIds.length) {
      return {
        data: [],
        pagination: {
          total: 0,
          totalPages: 0,
          hasNextPage: false,
          currentPage: page,
          hasPreviousPage: false,
        },
      };
    }
    const preferredId = entityIds[0];
    // // Step 1: Get user IDs with active profiles on this event
    // const activeUserIdsWithProfile = await prisma.profile.findMany({
    //   where: {
    //     eventId,
    //     // active: true,
    //     userId: { not: null },
    //   },
    //   select: { userId: true },
    // });

    // const activeUserIds = activeUserIdsWithProfile.map((p) => p.userId!);
    // If admin chats are requested, ensure organizer chats exist BEFORE fetching
    const provisionUserId = provisionOrganizerUserId || (isAdminChats ? preferredId : undefined);
    if (provisionUserId) {
      const organizersIds = await organizerIdsForEvent(eventId);

      // One query for existing user↔organizer chats, then create only missing.
      const existingAdminChats =
        organizersIds.length === 0
          ? []
          : await prisma.chat.findMany({
              where: {
                eventId,
                AND: [
                  {
                    participants: {
                      some: { participantId: provisionUserId },
                    },
                  },
                  {
                    participants: {
                      some: { participantId: { in: organizersIds } },
                    },
                  },
                ],
              },
              select: {
                participants: { select: { participantId: true } },
              },
            });

      const existingOrganizerIds = new Set<string>();
      for (const chat of existingAdminChats) {
        for (const p of chat.participants) {
          if (
            p.participantId !== provisionUserId &&
            organizersIds.includes(p.participantId)
          ) {
            existingOrganizerIds.add(p.participantId);
          }
        }
      }

      const missingOrganizerIds = organizersIds.filter(
        (id) => !existingOrganizerIds.has(id)
      );

      if (missingOrganizerIds.length > 0) {
        await Promise.all(
          missingOrganizerIds.map((organizerId) =>
            createChat({
              name: "",
              eventId,
              participantIds: [
                { id: provisionUserId, type: ChatParticipantType.USER },
                { id: organizerId, type: ChatParticipantType.ORGANIZATION },
              ],
            })
          )
        );
      }
    }

    // Step 2: Get chats where:
    // - any of the actor's entity ids is a participant (user + company)
    // - eventId matches
    // Booth-admin inbox: company threads + this user's ORGANIZATION threads, not personal USER DMs.
    const companyIds = provisionOrganizerUserId
      ? entityIds.filter((id) => id !== provisionOrganizerUserId)
      : [];
    const where: Prisma.ChatWhereInput = {
      AND: [
        {
          eventId,
        },
        provisionOrganizerUserId
          ? {
              OR: [
                ...(companyIds.length
                  ? [
                      {
                        participants: {
                          some: { participantId: { in: companyIds } },
                        },
                      },
                    ]
                  : []),
                {
                  AND: [
                    {
                      participants: {
                        some: { participantId: provisionOrganizerUserId },
                      },
                    },
                    {
                      participants: {
                        some: { type: ChatParticipantType.ORGANIZATION },
                      },
                    },
                  ],
                },
              ],
            }
          : {
              participants: {
                some: {
                  participantId: { in: entityIds },
                },
              },
            },
        ...(companyFacingOnly
          ? ([
              {
                participants: {
                  some: {
                    type: { not: ChatParticipantType.USER },
                    participantId: { notIn: entityIds },
                  },
                },
              },
            ] as Prisma.ChatWhereInput[])
          : []),
      ],
    };

    const totalPromise = prisma.chat.count({ where });
    const rawData = await prisma.chat.findMany({
      where,
      orderBy: [{ lastMessageAt: "desc" }, { createdAt: "desc" }],
      skip: (page - 1) * limit,
      take: limit,
      include: {
        participants: {
          include: {
            exhibitor: {
              include: {
                translations: true,
              },
            },
            sponsor: {
              include: {
                translations: true,
              },
            },
            user: {
              include: {
                profiles: {
                  where: {
                    eventId,
                  },
                  orderBy: {
                    createdAt: "desc",
                  },
                  take: 1,
                },
              },
            },
          },
        },
        messages: {
          include: {
            exhibitor: true,
            user: {
              include: {
                profiles: {
                  where: {
                    eventId,
                  },
                  orderBy: {
                    createdAt: "desc",
                  },
                  take: 1,
                },
              },
            },
          },
          take: 1,
          orderBy: { createdAt: "desc" },
        },
      },
    });

    // The query is already newest-first; only reconcile legacy user/company aliases
    // inside this page.
    const data = dedupeChatsByLogicalPeer(rawData, entityIds, preferredId);
    const total = await totalPromise;
    const hasNextPage = page * limit < total;
    const unreadRows = data.length
      ? await prisma.message.groupBy({
          by: ["chatId"],
          where: {
            chatId: { in: data.map((chat) => chat.id) },
            isRead: false,
            senderId: { notIn: entityIds },
          },
          _count: { _all: true },
        })
      : [];
    const unreadByChatId = Object.fromEntries(
      unreadRows.map((row) => [row.chatId, row._count._all])
    );
    return {
      data: data.map((chat) => ({
        ...chat,
        unreadCount: unreadByChatId[chat.id] ?? 0,
      })),
      pagination: {
        total,
        totalPages: Math.ceil(total / limit),
        hasNextPage,
        currentPage: page,
        hasPreviousPage: page > 1,
      },
    };
  } catch (error) {
    console.error("Error fetching chat:", error);
    throw new Error("Failed to fetch chat");
  }
};

export const getChatById = async (id: string) => {
  try {
    return await prisma.chat.findUnique({
      where: { id },
      include: {
        participants: {
          include: {
            exhibitor: {
              include: { translations: true },
            },
            sponsor: {
              include: { translations: true },
            },
            user: {
              include: {
                profiles: {
                  orderBy: { createdAt: "desc" },
                  take: 1,
                },
              },
            },
          },
        },
      },
    });
  } catch (error) {
    console.error("Error fetching chat:", error);
    throw new Error("Failed to fetch chat");
  }
};
